import { useState, useEffect } from "react";
import { JobCard } from "./JobCard";
import { List, LayoutGrid } from "lucide-react";

const STORAGE_KEY = "f2j_jobs_layout";

/**
 * Reusable jobs listing with a "list ↔ grid" toggle.
 * - list: vertical stack (space-y-4), full-width cards.
 * - grid: responsive grid (2 cols on md, 3 on xl).
 * Preference is persisted in localStorage (per browser).
 * Uses the existing JobCard component — no visual changes to individual cards.
 */
export function JobsView({ jobs, testIdPrefix = "jobs", defaultLayout = "list", showToggle = true, showCount = true }) {
  const [layout, setLayout] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "list" || stored === "grid") return stored;
    } catch { /* ignore */ }
    return defaultLayout;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, layout); } catch { /* ignore */ }
  }, [layout]);

  const total = jobs.length;

  return (
    <div>
      {(showToggle || showCount) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {showCount ? (
            <p className="text-sm text-slate-500" data-testid={`${testIdPrefix}-count`}>
              <span className="font-heading font-black tracking-tighter text-lg text-slate-900">{total}</span> vaga{total !== 1 ? "s" : ""}
            </p>
          ) : <span />}
          {showToggle && (
            <div className="inline-flex items-center rounded-full border border-black/10 bg-white p-1 gap-1" role="group" aria-label="Vista de listagem">
              <button
                type="button"
                data-testid={`${testIdPrefix}-view-list`}
                onClick={() => setLayout("list")}
                aria-pressed={layout === "list"}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  layout === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <List className="w-3.5 h-3.5" /> Lista
              </button>
              <button
                type="button"
                data-testid={`${testIdPrefix}-view-grid`}
                onClick={() => setLayout("grid")}
                aria-pressed={layout === "grid"}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  layout === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grelha
              </button>
            </div>
          )}
        </div>
      )}

      {layout === "grid" ? (
        <div data-testid={`${testIdPrefix}-grid`} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((j) => <JobCard key={j.id} job={j} variant="grid" />)}
        </div>
      ) : (
        <div data-testid={`${testIdPrefix}-list`} className="space-y-4">
          {jobs.map((j) => <JobCard key={j.id} job={j} variant="list" />)}
        </div>
      )}
    </div>
  );
}
